import { PaperAirplaneIcon } from '@heroicons/react/solid'
import { useMeeting } from '@videosdk.live/react-sdk'
import React, { useEffect, useRef, useState } from 'react'
import { formatAMPM, nameTruncated } from '../../utils/helper'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useChat } from '@/contexts/ChatProvider'
import { useIOSScrollFix } from '@/hooks/useIOSScrollFix'

type ChatItem = {
  senderId: string
  senderName: string
  text: string
  timestamp: number | string | Date
}

const ChatMessage = ({ senderId, senderName, text, timestamp }: ChatItem) => {
  const mMeeting = useMeeting()
  const localParticipantId = mMeeting?.localParticipant?.id
  const localSender = localParticipantId === senderId

  return (
    <div className={`flex ${localSender ? 'justify-end' : 'justify-start'} mt-2 max-w-full`}>
      <div
        className={`flex ${localSender ? 'items-end' : 'items-start'} flex-col py-1 px-2 rounded-sm bg-gray-200 dark:bg-gray-700`}
      >
        <p className="text-xs dark:text-gray-50">
          {localSender ? 'You' : nameTruncated(senderName ?? 'Unknown', 15)}
        </p>
        <p className="inline-block whitespace-pre-wrap break-words font-semibold dark:text-white text-sm">
          {text}
        </p>
        <p className="mt-0.5 text-xs italic dark:text-gray-50">{formatAMPM(new Date(timestamp))}</p>
      </div>
    </div>
  )
}

const ChatInput: React.FC = () => {
  const [message, setMessage] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const { currentUser } = useHallUsers()
  const { sendMessage } = useChat()
  const mMeeting = useMeeting()

  const fallbackId = mMeeting?.localParticipant?.id
  const fallbackName =
    mMeeting?.localParticipant?.displayName ||
    (typeof (mMeeting as any)?.localParticipant?.name === 'string'
      ? (mMeeting as any).localParticipant.name
      : undefined)

  const senderId = currentUser?.id || fallbackId
  const senderName = currentUser?.displayName || fallbackName || 'Anonymous'
  const canSend = !!senderId && message.trim().length > 0

  const handleSend = async () => {
    const text = message.trim()
    if (!text || !senderId) return
    await sendMessage({ text, senderName, senderId })
    setMessage('')
    input.current?.focus()
  }

  return (
    <div className="w-full px-2 py-2 shrink-0 bg-background border-t border-gray-300 dark:border-gray-700">
      <div className="relative w-full">
        <span className="absolute inset-y-0 right-0 flex mr-2 rotate-90">
          <button
            disabled={!canSend}
            type="button"
            className="p-1 focus:outline-none disabled:opacity-50"
            onClick={handleSend}
            aria-label="Send message"
          >
            <PaperAirplaneIcon
              className={`w-6 h-6 ${canSend ? 'dark:text-white' : 'text-gray-500'}`}
            />
          </button>
        </span>
        <input
          ref={input}
          type="text"
          className="py-2 text-base border border-gray-400 focus:border-green-500 bg-gray-200 dark:bg-background rounded pr-8 pl-2 focus:outline-none w-full"
          placeholder="Write your message"
          autoComplete="off"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
        />
      </div>
    </div>
  )
}

const ChatMessages: React.FC = () => {
  const listRef = useRef<HTMLDivElement | null>(null)
  const { messages } = useChat()
  useIOSScrollFix(listRef)

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages])

  return (
    <div ref={listRef} className="flex-1 overflow-y-auto min-h-0 ios-scroll noScrollbar">
      <div className="p-2">
        {messages?.length ? (
          messages.map((msg: ChatItem, i: number) => (
            <ChatMessage key={`chat_item_${i}`} {...msg} />
          ))
        ) : (
          <p className="p-2 text-xs">No messages</p>
        )}
      </div>
    </div>
  )
}

export const ChatPanel: React.FC<{ panelHeight?: number }> = () => {
  return (
    <div className="h-full flex flex-col min-h-0 pb-[env(safe-area-inset-bottom)]">
      <ChatMessages />
      <ChatInput />
    </div>
  )
}

export default ChatPanel
